import React, { useState, useMemo } from "react";
import { Plus, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { VisitFormDialog } from "./VisitFormDialog";
import { PendingVisitApprovalDialog } from "./PendingVisitApprovalDialog";
import { VisitStats } from "./visits/VisitStats";
import { VisitFilters } from "./visits/VisitFilters";
import { PendingVisitsTable } from "./visits/PendingVisitsTable";
import { VisitsTable } from "./visits/VisitsTable";
import { FailedEmailsTable } from "./visits/FailedEmailsTable";
import { API } from "../utils/helpers";

export const VisitManagement = ({
  visits, setVisits, sites, setSites, studies, setStudies,
  failedEmails, setFailedEmails, pendingVisits, setPendingVisits,
  isAddVisitOpen, setIsAddVisitOpen, isEditVisitOpen, setIsEditVisitOpen,
  selectedVisit, setSelectedVisit, isPendingVisitOpen, setIsPendingVisitOpen,
  selectedPendingVisit, setSelectedPendingVisit,
  searchSites, searchStudies, getStudyAutoPopulateData, getSiteAutoPopulateData,
}) => {
  const [filters, setFilters] = useState({ site_id: "", study_id: "", visit_type: "", status: "", pi_name: "" });
  const [pullingEmails, setPullingEmails] = useState(false);

  const filteredVisits = useMemo(() => visits.filter(v =>
    (!filters.site_id || v.site_id === filters.site_id) &&
    (!filters.study_id || v.study_id === filters.study_id) &&
    (!filters.visit_type || v.visit_type === filters.visit_type) &&
    (!filters.status || v.status === filters.status) &&
    (!filters.pi_name || v.pi_name?.toLowerCase().includes(filters.pi_name.toLowerCase()))
  ), [visits, filters]);

  const pullEmails = async () => {
    setPullingEmails(true);
    try {
      await axios.post(`${API}/process-emails?days_back=7`);
      const [vRes, pRes, fRes] = await Promise.all([
        axios.get(`${API}/visits`),
        axios.get(`${API}/pending-visits`),
        axios.get(`${API}/failed-emails`),
      ]);
      const newVisits = vRes.data.length - visits.length;
      const newPending = pRes.data.length - pendingVisits.length;
      setVisits(vRes.data);
      setPendingVisits(pRes.data);
      setFailedEmails(fRes.data);
      if (newVisits > 0 || newPending > 0) {
        toast.success(`Found ${newVisits} new visits and ${newPending} pending reviews from email`);
      } else {
        toast.info("No new calendar emails found in the last 7 days");
      }
    } catch {
      toast.error("Failed to process emails. Check email configuration.");
    } finally {
      setPullingEmails(false);
    }
  };

  const createVisit = async (visitData) => {
    try {
      const response = await axios.post(`${API}/visits`, visitData);
      setVisits([...visits, response.data]);
      setIsAddVisitOpen(false);
      toast.success("Visit created successfully!");
      const [sitesRes, studiesRes] = await Promise.all([axios.get(`${API}/sites`), axios.get(`${API}/studies`)]);
      setSites(sitesRes.data);
      setStudies(studiesRes.data);
    } catch {
      toast.error("Failed to create visit");
    }
  };

  const updateVisit = async (visitId, visitData) => {
    try {
      const response = await axios.put(`${API}/visits/${visitId}`, visitData);
      setVisits(visits.map(v => v.id === visitId ? response.data : v));
      setIsEditVisitOpen(false);
      setSelectedVisit(null);
      toast.success("Visit updated successfully!");
    } catch {
      toast.error("Failed to update visit");
    }
  };

  const deleteVisit = async (visitId) => {
    try {
      await axios.delete(`${API}/visits/${visitId}`);
      setVisits(visits.filter(v => v.id !== visitId));
      toast.success("Visit deleted successfully!");
    } catch {
      toast.error("Failed to delete visit");
    }
  };

  const approvePendingVisit = async (pendingId, approvalData) => {
    try {
      await axios.post(`${API}/pending-visits/${pendingId}/approve`, approvalData);
      setPendingVisits(pendingVisits.filter(v => v.id !== pendingId));
      const visitsRes = await axios.get(`${API}/visits`);
      setVisits(visitsRes.data);
      toast.success("Pending visit approved and tasks created!");
    } catch {
      toast.error("Failed to approve pending visit");
    }
  };

  const rejectPendingVisit = async (pendingId) => {
    try {
      await axios.delete(`${API}/pending-visits/${pendingId}`);
      setPendingVisits(pendingVisits.filter(v => v.id !== pendingId));
      toast.success("Pending visit rejected!");
    } catch {
      toast.error("Failed to reject pending visit");
    }
  };

  const resolveFailedEmail = async (emailId) => {
    try {
      await axios.delete(`${API}/failed-emails/${emailId}`);
      setFailedEmails(failedEmails.filter(e => e.id !== emailId));
      toast.success("Failed email resolved!");
    } catch {
      toast.error("Failed to resolve email");
    }
  };

  const handleReviewPending = (pendingVisit) => {
    setSelectedPendingVisit(pendingVisit);
    setIsPendingVisitOpen(true);
  };

  const handleEditVisit = (visit) => {
    setSelectedVisit(visit);
    setIsEditVisitOpen(true);
  };

  return (
    <div data-testid="visit-management" className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Visit Management</h1>
          <p className="text-gray-600">Monitor and manage clinical visits from automated email processing</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={pullEmails}
            disabled={pullingEmails}
            variant="outline"
            className="flex items-center gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="pull-emails-btn"
          >
            <Mail className={`h-4 w-4 ${pullingEmails ? 'animate-spin' : ''}`} />
            {pullingEmails ? 'Checking...' : 'Pull Emails'}
          </Button>
          <Button onClick={() => setIsAddVisitOpen(true)} className="flex items-center gap-2 bg-green-600 hover:bg-green-700" data-testid="add-visit-btn">
            <Plus className="h-4 w-4" /> Add Visit
          </Button>
        </div>
      </div>

      <VisitStats visits={visits} pendingVisits={pendingVisits} />

      <VisitFilters filters={filters} setFilters={setFilters} sites={sites} studies={studies} />

      <PendingVisitsTable pendingVisits={pendingVisits} onReview={handleReviewPending} onReject={rejectPendingVisit} />

      <VisitsTable visits={filteredVisits} sites={sites} studies={studies} onEdit={handleEditVisit} onDelete={deleteVisit} />

      <FailedEmailsTable failedEmails={failedEmails} onResolve={resolveFailedEmail} />

      <VisitFormDialog
        isOpen={isAddVisitOpen}
        onClose={() => setIsAddVisitOpen(false)}
        onSubmit={createVisit}
        sites={sites}
        studies={studies}
        title="Add New Visit"
        searchSites={searchSites}
        searchStudies={searchStudies}
        getStudyAutoPopulateData={getStudyAutoPopulateData}
        getSiteAutoPopulateData={getSiteAutoPopulateData}
      />

      <VisitFormDialog
        isOpen={isEditVisitOpen}
        onClose={() => { setIsEditVisitOpen(false); setSelectedVisit(null); }}
        onSubmit={(data) => updateVisit(selectedVisit.id, data)}
        sites={sites}
        studies={studies}
        visit={selectedVisit}
        title="Edit Visit"
        searchSites={searchSites}
        searchStudies={searchStudies}
        getStudyAutoPopulateData={getStudyAutoPopulateData}
        getSiteAutoPopulateData={getSiteAutoPopulateData}
      />

      <PendingVisitApprovalDialog
        isOpen={isPendingVisitOpen}
        onClose={() => { setIsPendingVisitOpen(false); setSelectedPendingVisit(null); }}
        onApprove={approvePendingVisit}
        pendingVisit={selectedPendingVisit}
        sites={sites}
        studies={studies}
      />
    </div>
  );
};
